import { type CSSProperties, type ReactElement, useEffect, useRef, useState } from 'react';
// Modules
import { EmblaCarouselType } from 'embla-carousel';
// Design System
import { Slides, SlidesItem } from '@utmb/design-system/slides/Slides';
import { Flag } from '@utmb/design-system/flag/Flag';
import { Font } from '@utmb/design-system/text/font';
import Countdown from '@utmb/design-system/countdown-remake/Countdown';
import { useCountdown } from '@utmb/design-system/countdown-remake/useCountdown';
import { Link } from '@utmb/design-system/link/link';
// Context
import { useGlobal } from '@utmb/contexts/GlobalContext';
// Types
import EventView from '@utmb/types/view/EventView';
// Styles
import styles from './next-event-slider.module.scss';

const AUTOPLAY_DELAY = 7000;

/**
 * Returns the next upcoming event (the not-yet-started one starting soonest)
 * along with every other not-yet-started event whose date range overlaps it.
 * Events already running are excluded — their countdown would be over.
 */
export function selectNextEvents(events: EventView[]): EventView[] {
    const now = Date.now();

    const upcoming = (events ?? [])
        .filter((event) => event.dateBegin && event.dateEnd)
        .map((event) => ({
            event,
            begin: new Date(event.dateBegin).getTime(),
            end: new Date(event.dateEnd).getTime(),
        }))
        .filter((item) => item.begin > now)
        .sort((a, b) => a.begin - b.begin);

    if (upcoming.length === 0) return [];

    const anchor = upcoming[0];

    // Overlap: two ranges overlap when each starts before the other ends.
    return upcoming.filter((item) => item.begin <= anchor.end && anchor.begin <= item.end).map((item) => item.event);
}

/**
 * Live view of selectNextEvents: schedules a re-render for the moment the
 * soonest event starts, so its slide (and the whole banner once none are
 * left) drops without a page reload.
 */
export function useNextEvents(): EventView[] {
    const { global } = useGlobal();
    const [, refresh] = useState(0);

    const events = selectNextEvents(global?.eventsTopBar ?? []);
    const nextStart = events.length > 0 ? Math.min(...events.map((event) => new Date(event.dateBegin).getTime())) : null;

    useEffect(() => {
        if (nextStart === null) return;
        // +1s so the recompute lands strictly after the start; capped because
        // setTimeout overflows above ~24.8 days.
        const delay = Math.min(nextStart - Date.now() + 1000, 0x7fffffff);
        const id = setTimeout(() => refresh((tick) => tick + 1), Math.max(delay, 0));
        return () => clearTimeout(id);
    }, [nextStart]);

    return events;
}

interface AutoplayProps {
    embla: EmblaCarouselType | undefined;
    paused: boolean;
}

/** Headless helper driving the embla instance exposed by <Slides />. */
function Autoplay({ embla, paused }: AutoplayProps): null {
    useEffect(() => {
        if (!embla || paused) return;

        const id = setInterval(() => {
            if (embla.canScrollNext()) embla.scrollNext();
            else embla.scrollTo(0);
        }, AUTOPLAY_DELAY);

        return () => clearInterval(id);
    }, [embla, paused]);

    return null;
}

function EventChip({ event }: { event: EventView }): ReactElement {
    const info = (
        <div className={styles.info}>
            <Font.FuturaHeavy as="div" desktop="16" mobile="14" className={styles.name}>
                {event.title}
            </Font.FuturaHeavy>
            <div className={styles.meta}>
                {event.countryCode && <Flag className={styles.flag} isoCode={event.countryCode} />}
                {event.dateFormated && (
                    <Font.FuturaMedium as="span" desktop="14" mobile="12" className={styles.date}>
                        {event.dateFormated}
                    </Font.FuturaMedium>
                )}
                {/* {event.placeName && <span className={styles.place}>· {event.placeName}</span>} */}
            </div>
        </div>
    );

    // <Link /> renders nothing without an href — keep the plain chip for events without a website.
    if (!event.url) return <div className={styles.chip}>{info}</div>;

    return (
        <Link href={event.url} className={styles.chip}>
            {info}
        </Link>
    );
}

interface EventSlideProps {
    event: EventView;
    measureRef?: (el: HTMLDivElement | null) => void;
}

function EventSlide({ event, measureRef }: EventSlideProps): ReactElement {
    const targetDate = new Date(event.dateBegin);
    // Started events are filtered out upstream (useNextEvents); this guard only
    // covers the brief window between the start and the slide being dropped.
    const [hasStarted] = useCountdown(targetDate);

    return (
        <div ref={measureRef} className={styles.eventSlide}>
            {!hasStarted && (
                <div className={styles.slideCountdown}>
                    <Countdown targetDate={targetDate} variant="tudor" />
                </div>
            )}
            <EventChip event={event} />
        </div>
    );
}

/** Headless helper: re-measures embla whenever the viewport width we control changes. */
function ReInit({ embla, watch }: { embla: EmblaCarouselType | undefined; watch: number | null }): null {
    useEffect(() => {
        embla?.reInit();
    }, [embla, watch]);

    return null;
}

export function NextEventSlider(): ReactElement | null {
    const [paused, setPaused] = useState(false);

    // Fit-content viewport: slides shrink-wrap (.eventSlide is fit-content), we
    // track the widest one and hand its width to the stylesheet, which uses it
    // as an extra bound on the carousel viewport. Re-measures on content
    // changes (font load, countdown dropping a unit) via ResizeObserver.
    const slideElements = useRef<Map<number, HTMLDivElement>>(new Map());
    const [contentWidth, setContentWidth] = useState<number | null>(null);

    const events = useNextEvents();
    const eventCount = events.length;

    useEffect(() => {
        if (eventCount < 2 || typeof ResizeObserver === 'undefined') return;

        const measure = (): void => {
            let widest = 0;
            slideElements.current.forEach((slideContent) => {
                // Include the .slide horizontal insets so the viewport width maps
                // 1:1 to a full slide.
                const slide = slideContent.parentElement;
                const slideStyle = slide ? getComputedStyle(slide) : undefined;
                const insets = slideStyle ? parseFloat(slideStyle.paddingLeft) + parseFloat(slideStyle.paddingRight) : 0;
                widest = Math.max(widest, Math.ceil(slideContent.offsetWidth + insets));
            });
            if (widest > 0) setContentWidth(widest);
        };

        measure();
        const observer = new ResizeObserver(measure);
        slideElements.current.forEach((slideContent) => observer.observe(slideContent));
        return () => observer.disconnect();
    }, [eventCount]);

    if (eventCount === 0) return null;

    if (eventCount === 1) {
        return (
            <div className={styles.nextEventSlider}>
                <EventSlide event={events[0]} />
            </div>
        );
    }

    const fitContentStyle = contentWidth ? ({ '--next-event-slider-width': `${contentWidth}px` } as CSSProperties) : undefined;

    return (
        <div className={styles.nextEventSlider} style={fitContentStyle} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <Slides className={styles.viewport} carouselOptions={{ loop: false, align: 'start', draggable: true }} dots dotsClassName={styles.dots}>
                {({ embla }) => (
                    <>
                        {events.map((event, index) => (
                            <SlidesItem key={index} className="embla-slide-max" composeClassName={styles.slide}>
                                <EventSlide
                                    event={event}
                                    measureRef={(el): void => {
                                        if (el) slideElements.current.set(index, el);
                                        else slideElements.current.delete(index);
                                    }}
                                />
                            </SlidesItem>
                        ))}
                        <Autoplay embla={embla} paused={paused} />
                        <ReInit embla={embla} watch={contentWidth} />
                    </>
                )}
            </Slides>
        </div>
    );
}
